"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import IntentLink from "./IntentLink";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Languages, Menu, Moon, Search, Sun } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { Locale } from "@/i18n-config";
import { getDesktopNav, getMobileTabs, groupNavItems, resolveActiveId, type NavViewerState } from "@/lib/navigation";
import { v2Surface } from "./nav-v2";
import { useAuthStore } from "@/store/useAuthStore";
import { useTheme } from "@/hooks/useTheme";
import { localePath } from "@/lib/locale-path";
import SearchPanel from "./SearchPanel";
import CreateFab from "./CreateFab";
import MenuSheet from "./MenuSheet";
import { Toaster } from "./ui";
import AccountMenu from "./AccountMenu";
import WalletMenu from "./WalletMenu";
import NotificationsMenu from "./NotificationsMenu";
import FooterV2 from "./Footer";
import type { Currency } from "@/features/currencies/types";
import { tickerLoop, tickerRows } from "./tickerRows";
import "./v2.css";
import "./shell.css";

/**
 * THE v2 APP SHELL.
 *
 * A side rail that is an icon strip until you reach for it, a 52px top bar,
 * one live ticker, a gold reading-progress rule, and a floating tab bar on
 * phones. Glass is used in exactly these places and nowhere else — they are
 * the only surfaces that float over content, which is the only thing glass is
 * honestly for, and they all use the one recipe (`--v2-glass-*`).
 *
 * THIS IS THE CHROME FOR EVERY ROUTE. It is mounted once by `ClientLayout`
 * and wraps `children`; the only routes without it are the genuine fullscreen
 * takeovers (`/auth/*`, `/connect-wallet`).
 *
 * THE NAV LISTS ARE NOT WRITTEN HERE. Every surface reads
 * `src/lib/navigation` — the same catalogue the legacy sidebar, tab bar,
 * drawer and footer read. That matters for more than tidiness: an entry's
 * audience is DERIVED from the middleware's protected-route list, so a route
 * a guest cannot open cannot appear in the rail, and a signed-in viewer's
 * extra destinations (dashboard, wallet, portfolio, messages, settings)
 * appear without a second hand-written list to keep in sync. The hard-coded
 * arrays that used to live in this file showed a guest's set to everyone.
 *
 * THE THEME AND LOCALE CONTROLS LIVE IN THE RAIL, ONCE. They were in the top
 * bar as well, which is the same control twice on the same screen. The rail
 * starts at 1024px, so below that they are reached through the phone menu —
 * see `MenuSheet`.
 *
 * Everything positions with logical properties (`inset-inline-start`,
 * `padding-inline`, `border-inline-end`), so RTL and LTR come from one
 * stylesheet rather than a mirrored copy.
 */

type Props = {
  lang: Locale;
  currencies: Currency[];
  dict: Dictionary;
  children: React.ReactNode;
  /** A site-wide notice, above the top bar inside the page column (so the desktop rail never covers it). */
  notice?: React.ReactNode;
};

/**
 * THE REAL BRAND MARK, not a glyph that resembles one.
 *
 * Both themes are rendered and swapped in CSS rather than picked in JS from
 * the theme store. The store is persisted and rehydrates after paint, so a JS
 * choice shows the light logo for a frame on a dark-theme visit; the `.dark`
 * class is on `<html>` before the first paint, so CSS gets it right the first
 * time. Two files, ~500 bytes each — cheaper than the flash.
 *
 * `width`/`height` are the files' intrinsic pixels (111×40 and 25×40), so the
 * box is reserved before the bytes land.
 */
function Logo(t0) {
  const $ = _c(10);
  const {
    full,
    className
  } = t0;
  const w = full ? 111 : 25;
  const dir = full ? "logo" : "logo-small";
  const t1 = className ? `v2-logo ${className}` : "v2-logo";
  const t2 = `/assets/images/logos/light/${dir}.webp`;
  let t3;
  if ($[0] !== t2 || $[1] !== w) {
    t3 = <Image className="v2-logo-img v2-logo-img--light" src={t2} alt="NFTBaz" width={w} height={40} />;
    $[0] = t2;
    $[1] = w;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = `/assets/images/logos/dark/${full ? "logo-light" : "logo-small-light"}.webp`;
  let t5;
  if ($[3] !== t4 || $[4] !== w) {
    t5 = <Image className="v2-logo-img v2-logo-img--dark" src={t4} alt="" aria-hidden={true} width={w} height={40} />;
    $[3] = t4;
    $[4] = w;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t3 || $[8] !== t5) {
    t6 = <span className={t1}>{t3}{t5}</span>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function Ticker(t0) {
  const $ = _c(18);
  const {
    currencies,
    isFa
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== currencies || $[1] !== isFa) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rows = tickerRows(currencies);
      if (rows.length === 0) {
        t6 = null;
        break bb0;
      }
      const fmt = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US", {
        maximumFractionDigits: 0
      });
      const usd = new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2
      });
      const {
        copies,
        seconds
      } = tickerLoop(rows.length);
      const loop = Array.from({
        length: copies
      }, () => rows).flat();
      t4 = "v2-ticker";
      t5 = isFa ? "\u0646\u0631\u062E \u0644\u062D\u0638\u0647\u200C\u0627\u06CC \u0627\u0631\u0632\u0647\u0627" : "Live currency rates";
      t1 = "v2-marquee-track";
      const t7 = `${seconds}s`;
      if ($[8] !== t7) {
        t2 = {
          animationDuration: t7
        };
        $[8] = t7;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      t3 = loop.map((c, i) => <span className="v2-tick" key={`${c.symbol}-${i}`} aria-hidden={i >= rows.length}>{c.image ? <Image className="v2-tick-img" src={c.image} alt="" width={13} height={13} /> : null}<span className="v2-tick-sym">{String(c.symbol ?? "").toUpperCase()}</span><span className="v2-tick-val">{isFa ? fmt.format(Number(c.price_sell_irr)) : usd.format(Number(c.last_price))}</span><span className="v2-tick-unit">{isFa ? "\u062A\u0648\u0645\u0627\u0646" : "USDT"}</span></span>);
    }
    $[0] = currencies;
    $[1] = isFa;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t7 = <div className={t1} style={t2}>{t3}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t7) {
    t8 = <div className={t4} aria-label={t5}>{t7}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
export default function HomeV2Shell({
  lang,
  currencies,
  dict,
  children,
  notice
}: Props) {
  const isFa = lang === "fa";
  const pathname = usePathname();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // The rail is closed until its button opens it — never on hover.
  const [railOpen, setRailOpen] = useState(false);
  const rail = useRef<HTMLElement>(null);
  const railToggle = useRef<HTMLButtonElement>(null);
  const progress = useRef<HTMLElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  useEffect(() => setMounted(true), []);
  useEffect(() => setRailOpen(false), [pathname]);
  // Open, it closes on a press anywhere outside it, or on Escape (focus back on its button).
  useEffect(() => {
    if (!railOpen) return;
    // The toggle lives outside the rail (a portal on its edge): a press on it is its own, not an «outside».
    const onDown = (e: PointerEvent) => {
      const at = e.target as Node;
      if (!rail.current?.contains(at) && !railToggle.current?.contains(at)) setRailOpen(false);
    };
    const onKey = (e_0: KeyboardEvent) => {
      if (e_0.key === "Escape") {
        setRailOpen(false);
        railToggle.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [railOpen]);
  const user = useAuthStore(s => s.user);
  /**
   * Until the auth store has rehydrated, the viewer is a guest as far as the
   * nav is concerned. This is not a guess about who they are — it is the only
   * state that is safe to render on the server and on the first client frame,
   * because a signed-in set rendered before hydration would be wrong for every
   * signed-out visitor and would have to be taken away again.
   */
  const token = useAuthStore(s_0 => s_0.token);
  const signedIn = mounted && !!user && !!token;
  const viewer: NavViewerState = {
    isAuthenticated: signedIn
  };

  /**
   * `v2Surface` is the presentation pass: it swaps the catalogue's legacy
   * icons for this design system's, and drops the Dashboard entry. See
   * `nav-v2.ts` — the catalogue still decides what is listed and to whom.
   */
  const railItems = useMemo(() => v2Surface(getDesktopNav(viewer, lang, dict), lang, dict), [viewer.isAuthenticated, lang, dict]);
  const railSections = useMemo(() => groupNavItems(railItems), [railItems]);
  const tabs = useMemo(() => v2Surface(getMobileTabs(viewer, lang, dict), lang, dict), [viewer.isAuthenticated, lang, dict]);
  const activeId = useMemo(() => resolveActiveId(railItems, pathname, lang), [railItems, pathname, lang]);
  /** The tab row and the drawer can hold entries the rail does not. */
  const tabActiveId = useMemo(() => resolveActiveId(tabs, pathname, lang), [tabs, pathname, lang]);

  /**
   * Reading progress, written straight to the element.
   *
   * NO REACT STATE. A scroll position in state is a re-render of this whole
   * subtree on every scroll frame; the value is only ever a number going into
   * one style property, so it goes there directly through a ref.
   *
   * NO LAYOUT READS EITHER. `scrollY`, `innerHeight` and
   * `documentElement.scrollHeight` are all cheap — none of them forces a
   * synchronous reflow — and the write is a `transform`, which the compositor
   * handles without a paint. The whole handler is one multiply and one string
   * assignment, coalesced to one per animation frame.
   */
  useEffect(() => {
    let frame = 0;
    let lastY = window.scrollY;
    let tickerOff = false;
    const paint = () => {
      frame = 0;
      const y = window.scrollY;
      const el = progress.current;
      if (el) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
        el.style.transform = `scaleX(${ratio})`;
      }

      /**
       * THE RATE TICKER RETRACTS WHILE YOU READ DOWNWARD.
       *
       * It is a marquee: useful when you arrive or come back up, pure motion
       * in the corner of the eye while you are reading a page. Down past
       * 140px it goes; any upward movement brings it back.
       *
       * THE 30px IT GIVES UP IS TAKEN BY A SPACER, NOT RECLAIMED. The header
       * is position:sticky, so it still occupies its height in normal
       * flow — shrinking it moves everything after it up by 30px, which while
       * scrolled reads as the page jumping. .v2-tick-gap grows by exactly
       * what the ticker loses, so the document's height never changes and
       * only the chrome moves.
       *
       * The 8px deadband is not decoration: without it, the sub-pixel jitter
       * of a trackpad flings the bar open and shut on a single gesture.
       */
      const root = shell.current;
      if (root) {
        const down = y > lastY + 8;
        const up = y < lastY - 8;
        if (down && y > 140 && !tickerOff) {
          tickerOff = true;
          root.dataset.ticker = "off";
        } else if ((up || y <= 140) && tickerOff) {
          tickerOff = false;
          delete root.dataset.ticker;
        }
        if (down || up) lastY = y;
      }
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(paint);
    };
    paint();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    window.addEventListener("resize", onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // The shortcut the bar advertises. Without this the ⌘K badge is a lie, and a
  // badge that promises a key that does nothing is worse than no badge.
  useEffect(() => {
    const onKey_0 = (e_1: KeyboardEvent) => {
      if ((e_1.metaKey || e_1.ctrlKey) && e_1.key.toLowerCase() === "k") {
        e_1.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey_0);
    return () => window.removeEventListener("keydown", onKey_0);
  }, []);
  const t = (fa: string, en: string) => isFa ? fa : en;
  const home = `/${lang}`;
  const otherLocale: Locale = isFa ? "en" : "fa";
  const languageHref = localePath(pathname, otherLocale);
  // Any item chosen in the rail closes it.
  const closeRail = () => setRailOpen(false);
  // A plain arrow toward where the rail's edge will go: out when closed, back when open (mirrored in Persian).
  const RailIcon = railOpen === isFa ? ChevronRight : ChevronLeft;
  return <div className="v2 v2-shell" ref={shell}>
      <div className="v2-lattice" aria-hidden />
      <div className="v2-wash" aria-hidden />

      {/* The rail's toggle, ON ITS EDGE: portalled out of the rail (which clips its overflow) so it can sit across the
          corner — half on the rail, half on the top bar — beside the logo, and travel with the edge as it opens. */}
      {mounted ? createPortal(<div className="v2 v2-pop-portal">
          <button type="button" ref={railToggle} className="v2-rail-edge v2-focus" data-open={railOpen ? "true" : undefined} aria-expanded={railOpen} aria-label={railOpen ? t("بستن منو", "Close menu") : t("باز کردن منو", "Open menu")} title={railOpen ? t("بستن منو", "Close menu") : t("باز کردن منو", "Open menu")} onClick={() => setRailOpen(o => !o)}>
            <RailIcon aria-hidden />
          </button>
        </div>, document.body) : null}

      {/* ---------- desktop rail ---------- */}
      <nav className="v2-railbar" ref={rail} aria-label={dict.navUi.mainNavigation} data-open={railOpen ? "true" : undefined}>
        <IntentLink className="v2-rail-logo v2-focus" href={home} aria-label="NFTBaz" onClick={closeRail}>
          <Logo full={false} className="v2-logo--mark" />
          <Logo full className="v2-logo--word" />
        </IntentLink>

        <div className="v2-rail-scroll">
          {railSections.map(section => <div className="v2-rail-group" key={section.group}>
              {/* The header is only legible once the rail is open; at 60px it
                  would be three clipped letters, so it is hidden rather than
                  truncated. `aria-hidden` because the grouping is already
                  carried by the list order and the labels themselves. */}
              <p className="v2-rail-head" aria-hidden>
                {dict.navGroups[section.group]}
              </p>
              {section.items.map(item => {
            const Icon = item.icon;
            return <IntentLink className="v2-rail-link v2-focus" href={item.href} key={item.id} aria-current={item.id === activeId ? "page" : undefined} onClick={closeRail}>
                    <Icon aria-hidden />
                    <span>{item.label}</span>
                  </IntentLink>;
          })}
            </div>)}
        </div>

        {/* THE ONLY PLACE these two controls exist on desktop. */}
        <div className="v2-rail-tools">
          <IntentLink className="v2-rail-link v2-focus" href={languageHref} hrefLang={otherLocale} onClick={closeRail}>
            <Languages aria-hidden />
            <span>{t("English", "فارسی")}</span>
          </IntentLink>

          <button type="button" className="v2-rail-link v2-rail-btn v2-focus" onClick={() => {
          toggleTheme();
          closeRail();
        }}>
            {mounted && theme === "dark" ? <Sun aria-hidden /> : <Moon aria-hidden />}
            <span>{t("تم", "Theme")}</span>
          </button>
        </div>
      </nav>

      <div className="v2-body">
        {notice}
        {/* ---------- top bar ---------- */}
        <header className="v2-top">
          <div className="v2-top-in">
            <IntentLink className="v2-top-mark v2-focus" href={home} aria-label="NFTBaz">
              <Logo full={false} />
            </IntentLink>

            <button type="button" className="v2-search v2-focus" onClick={() => setSearchOpen(true)} aria-haspopup="dialog" aria-expanded={searchOpen}>
              <Search aria-hidden />
              <span>{t("جست‌وجو…", "Search…")}</span>
              <span className="v2-kbd">⌘K</span>
            </button>

            {/*
              ONE OBJECT, NOT THREE.
               The wallet, the tray and the account used to sit in this row as
              three separately bordered controls of three different widths,
              with the unread badge floating outside the tray's box — landing
              exactly where that control's gold corner bracket lives, so the
              row had no top line. Bound into a single bordered cluster with
              hairline dividers, the bar carries one object with three cells,
              the badge moves inside it, and the brackets belong to the cluster.
               The hydration slot is owned HERE rather than by each control, so
              the three cannot disagree about which frame they appear on.
             */}
            {!mounted ? <span className="v2-acct v2-acct--slot" aria-hidden /> : signedIn ? <div className="v2-cluster">
                <WalletMenu lang={isFa ? "fa" : "en"} currencies={currencies} />
                <NotificationsMenu lang={isFa ? "fa" : "en"} />
                <AccountMenu lang={isFa ? "fa" : "en"} />
              </div> : <AccountMenu lang={isFa ? "fa" : "en"} />}
          </div>
          <Ticker currencies={currencies} isFa={isFa} />
          {/* Reading progress. `aria-hidden`: it is a restatement of the
              scrollbar, which assistive technology already exposes. */}
          <div className="v2-progress" aria-hidden>
            <i ref={progress} />
          </div>
        </header>
        {/* The ticker's height, held in reserve — see the scroll handler. */}
        <i className="v2-tick-gap" aria-hidden />

        {children}

        {/* The footer is chrome. It used to be rendered by the home page, which
            meant every other route had none the moment this shell became the
            app-wide one. */}
        <FooterV2 lang={isFa ? "fa" : "en"} dict={dict} />
      </div>

      <SearchPanel lang={isFa ? "fa" : "en"} open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CreateFab lang={isFa ? "fa" : "en"} />
      <Toaster dismissLabel={t("بستن", "Dismiss")} />

      {/* ---------- phone tab bar ---------- */}
      <nav className="v2-tabs" aria-label={dict.navUi.mobileNavigation}>
        {tabs.map(item_0 => {
        const Icon_0 = item_0.icon;
        return <IntentLink className="v2-tab v2-focus" href={item_0.href} key={item_0.id} aria-current={item_0.id === tabActiveId ? "page" : undefined}>
              <Icon_0 aria-hidden />
              {item_0.label}
            </IntentLink>;
      })}
        {/* The fifth slot. Everything the rail carries on desktop — including
            theme and locale — is behind this on a phone. */}
        <button type="button" className="v2-tab v2-focus" onClick={() => setMenuOpen(true)} aria-haspopup="dialog" aria-expanded={menuOpen}>
          <Menu aria-hidden />
          {dict.navUi.menu}
        </button>
      </nav>

      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} lang={lang} languageHref={languageHref} dict={dict} viewer={viewer} activeId={activeId} />

    </div>;
}